import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Column, Row, Expander } from '@amzn/meridian';

import { DEFAULT_MULTIPLIER } from './FlowPlannerGridConfig';
import { getCycleDisplayName } from '../../utils/helpers';
import ApplyAllInput from './shared/ApplyAllInput';
import ConfigPanel from './shared/ConfigPanel';

import './RateDistribution.css';

const PRIORITY_PROCESSES = [
  'ADTA Container Building',
  'ASL Induct Loader',
  'ASML Labeler',
  'Container Building',
  'Induct',
  'Stow'
];

const isRateEditable = (rate, hasOverride) => rate > 100000 || rate < 50 || hasOverride;

const RateInput = ({ value, onChange, onCommit }) => (
  <span className="fp-input rd-input rd-input-rate">
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit();
      }}
    />
  </span>
);

const RateDistribution = ({
  availableCycles,
  intervals,
  processRates,
  rateDistributions,
  onMultipliersChange,
  onTargetRateChange,
  joined = false
}) => {
  const sortedCycles = [...availableCycles].sort();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllRates, setShowAllRates] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(sortedCycles[0] || 'CYCLE_1');
  const [editingRate, setEditingRate] = useState({});
  const [editingMult, setEditingMult] = useState(null); // { process, slot, value }

  // Snapshot the initial rateDistributions on first load — used as the revert target.
  const initialDistRef = useRef(null);
  useEffect(() => {
    if (initialDistRef.current === null && rateDistributions) {
      initialDistRef.current = JSON.parse(JSON.stringify(rateDistributions));
    }
  }, [rateDistributions]);

  const getOriginalMult = (cycle, processName) =>
    initialDistRef.current?.[cycle]?.[processName]?.percentages || Array(intervals.length).fill(DEFAULT_MULTIPLIER);

  const isCellChanged = (cycle, processName, slotIndex, currentValue) => {
    const original = getOriginalMult(cycle, processName);
    return parseFloat(currentValue) !== parseFloat(original[slotIndex] ?? DEFAULT_MULTIPLIER);
  };

  const isRowChanged = (cycle, processName, currentMult) => {
    const original = getOriginalMult(cycle, processName);
    return currentMult.some((m, i) => parseFloat(m) !== parseFloat(original[i] ?? DEFAULT_MULTIPLIER));
  };

  const commitRateEdit = (processName) => {
    const key = `${selectedCycle}:${processName}`;
    const num = parseInt(editingRate[key], 10);
    if (!isNaN(num) && num >= 0) onTargetRateChange(selectedCycle, processName, num);
    setEditingRate((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    const sorted = [...availableCycles].sort();
    if (sorted.length > 0) setSelectedCycle((prev) => (sorted.includes(prev) ? prev : sorted[0]));
  }, [availableCycles]);

  useEffect(() => {
    setEditingRate({});
    setEditingMult(null);
  }, [selectedCycle]);

  const processRatesForCycle = useMemo(() => processRates[selectedCycle] || [], [processRates, selectedCycle]);
  const displayedProcesses = useMemo(() => {
    const sorted = [...processRatesForCycle].sort((a, b) => a.name.localeCompare(b.name));
    if (showAllRates) return sorted;
    const prioritySet = new Set(PRIORITY_PROCESSES);
    const priority = sorted.filter((p) => prioritySet.has(p.name));
    if (priority.length >= 5) return priority.slice(0, 5);
    const remaining = sorted.filter((p) => !prioritySet.has(p.name));
    return [...priority, ...remaining].slice(0, 5).sort((a, b) => a.name.localeCompare(b.name));
  }, [processRatesForCycle, showAllRates]);

  const handleMultiplierChange = (processName, slotIndex, value) => {
    const currentMult = [
      ...(rateDistributions[selectedCycle]?.[processName]?.percentages ||
        Array(intervals.length).fill(DEFAULT_MULTIPLIER))
    ];
    currentMult[slotIndex] = value;
    onMultipliersChange(selectedCycle, processName, currentMult);
  };

  const summary = useMemo(() => {
    if (!availableCycles.length) return [];
    return availableCycles
      .map((cycle) => {
        const rates = processRates[cycle] || [];
        const stow = rates.find((r) => r.name === 'Stow');
        const adta = rates.find((r) => r.name === 'ADTA Container Building');
        if (!stow && !adta) return null;
        const items = [];
        if (stow) items.push(`Stow ${stow.targetRate}`);
        if (adta) items.push(`ADTA ${adta.targetRate}`);
        return { cycle: getCycleDisplayName(cycle), rates: items.join(', ') };
      })
      .filter(Boolean);
  }, [processRates, availableCycles]);

  return (
    <ConfigPanel joined={joined} className="rd-config">
      <Row widths={['fill', 'fit']} alignmentVertical="center">
        <Column spacing="300" alignmentHorizontal="stretch" width="100%">
          <Expander
            open={isExpanded}
            onChange={setIsExpanded}
            title="Rate Distribution"
            type="inline"
            spacingInset="300">
            {/* Toolbar: cycle tabs + show-all toggle */}
            <div className="rd-toolbar">
              <div className="fp-config-tabs">
                {sortedCycles.map((cycle) => (
                  <button
                    key={cycle}
                    type="button"
                    className="fp-config-tab"
                    aria-pressed={selectedCycle === cycle}
                    onClick={() => setSelectedCycle(cycle)}>
                    {getCycleDisplayName(cycle)}
                  </button>
                ))}
              </div>
              <label className="rd-show-all">
                <input type="checkbox" checked={showAllRates} onChange={(e) => setShowAllRates(e.target.checked)} />
                Show all rates
              </label>
            </div>

            {/* Table */}
            {processRatesForCycle.length === 0 ? (
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                No rate data available. Configure Total Volume first.
              </span>
            ) : (
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table className="rd-table">
                  <thead>
                    <tr>
                      <th className="rd-th rd-th-process">Process</th>
                      <th className="rd-th rd-th-rate">Target Rate</th>
                      {intervals.map((interval, idx) => (
                        <th key={idx} className="rd-th rd-th-interval">
                          <span className="rd-slot-line">
                            <span className="rd-slot-primary">{interval.name}</span>
                            <span className="rd-slot-secondary">
                              {interval.startLabel}–{interval.endLabel}
                            </span>
                          </span>
                        </th>
                      ))}
                      <th className="rd-th rd-th-apply">Apply All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedProcesses.map((process) => {
                      const mult =
                        rateDistributions[selectedCycle]?.[process.name]?.percentages ||
                        Array(intervals.length).fill(DEFAULT_MULTIPLIER);
                      const rateKey = `${selectedCycle}:${process.name}`;
                      const isEditing = editingRate[rateKey] != null;
                      const editable = isRateEditable(
                        process.targetRate,
                        rateDistributions[selectedCycle]?.[process.name]?.targetRate != null
                      );

                      return (
                        <tr key={process.name} className="rd-row">
                          <td className="rd-cell rd-cell-process">
                            <span className="rd-process-name">{process.name}</span>
                          </td>
                          <td className="rd-cell rd-cell-rate">
                            {isEditing ? (
                              <RateInput
                                value={editingRate[rateKey]}
                                onChange={(value) => setEditingRate((prev) => ({ ...prev, [rateKey]: value }))}
                                onCommit={() => commitRateEdit(process.name)}
                              />
                            ) : (
                              <span
                                className={`rd-rate-display${editable ? ' rd-rate-editable' : ''}`}
                                onClick={
                                  editable
                                    ? () =>
                                        setEditingRate((prev) => ({ ...prev, [rateKey]: String(process.targetRate) }))
                                    : undefined
                                }>
                                {process.targetRate.toLocaleString()}
                              </span>
                            )}
                          </td>
                          {intervals.map((_, slotIndex) => {
                            const multiplier = mult[slotIndex] ?? DEFAULT_MULTIPLIER;
                            const plannedRate = Math.round(process.targetRate * parseFloat(multiplier));
                            const isEditingThis =
                              editingMult?.process === process.name && editingMult?.slot === slotIndex;
                            const cellChanged = isCellChanged(selectedCycle, process.name, slotIndex, multiplier);
                            return (
                              <td key={slotIndex} className="rd-cell rd-cell-interval">
                                <div className="rd-interval-stack">
                                  <span className="rd-planned-rate">{plannedRate.toLocaleString()}</span>
                                  {isEditingThis ? (
                                    <input
                                      className="rd-mult-edit"
                                      type="text"
                                      value={editingMult.value}
                                      autoFocus
                                      onChange={(e) => setEditingMult({ ...editingMult, value: e.target.value })}
                                      onBlur={(e) => {
                                        const v = parseFloat(e.target.value);
                                        if (!isNaN(v) && v > 0) handleMultiplierChange(process.name, slotIndex, v);
                                        setEditingMult(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          const v = parseFloat(editingMult.value);
                                          if (!isNaN(v) && v > 0) handleMultiplierChange(process.name, slotIndex, v);
                                          setEditingMult(null);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <span className="rd-mult-row">
                                      <span
                                        className="rd-multiplier"
                                        onClick={() =>
                                          setEditingMult({
                                            process: process.name,
                                            slot: slotIndex,
                                            value: String(multiplier)
                                          })
                                        }
                                        title="Click to edit multiplier">
                                        ×{multiplier}
                                      </span>
                                      {cellChanged && (
                                        <button
                                          type="button"
                                          className="rd-revert-cell"
                                          onClick={() => {
                                            const original = getOriginalMult(selectedCycle, process.name);
                                            handleMultiplierChange(
                                              process.name,
                                              slotIndex,
                                              original[slotIndex] ?? DEFAULT_MULTIPLIER
                                            );
                                          }}
                                          title="Revert to original">
                                          ↩
                                        </button>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td className="rd-cell rd-cell-apply">
                            <div className="rd-apply-wrap">
                              <span className="rd-apply-prefix">×</span>
                              <ApplyAllInput
                                min={0.01}
                                defaultValue="1"
                                inputClassName="fp-input rd-input"
                                btnClassName="rd-apply-btn"
                                onApply={(value) => {
                                  const newMult = Array(intervals.length).fill(value);
                                  onMultipliersChange(selectedCycle, process.name, newMult);
                                }}
                              />
                            </div>
                            {isRowChanged(selectedCycle, process.name, mult) && (
                              <button
                                type="button"
                                className="rd-revert-row"
                                onClick={() => {
                                  const original = getOriginalMult(selectedCycle, process.name);
                                  onMultipliersChange(selectedCycle, process.name, [...original]);
                                }}
                                title="Revert all intervals to original">
                                ↩ Revert
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Expander>
        </Column>
        {!isExpanded && summary.length > 0 && (
          <div className="fp-collapsed-summary">
            <div className="fp-collapsed-chips">
              {summary.map((item, idx) => (
                <span key={idx} className="fp-chip">
                  <span className="fp-chip-label">{item.cycle}</span>
                  <span className="fp-chip-value">{item.rates}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </Row>
    </ConfigPanel>
  );
};

export default RateDistribution;
